// ============================================================
// MANIC MEETER — Auth UI (Login / Register / Guest)
// ============================================================

import { loginAsGuest, login, register, getMe, logout, isLoggedIn } from "./api";
import { C } from "./i18n";

export interface AuthResult {
  loggedIn: boolean;
  isGuest: boolean;
  username?: string;
}

function createModal(id: string, content: string): HTMLElement {
  const overlay = document.createElement("div");
  overlay.id = id;
  overlay.className = "modal-overlay";
  overlay.innerHTML = `<div class="modal-box">${content}</div>`;
  document.body.appendChild(overlay);
  return overlay;
}

function removeModal(id: string): void {
  document.getElementById(id)?.remove();
}

function showError(containerId: string, msg: string): void {
  const el = document.querySelector(`#${containerId} .auth-error`) as HTMLElement | null;
  if (el) {
    el.textContent = msg;
    el.style.display = "block";
  }
}

/** Show login modal, returns Promise that resolves when user logs in */
export function showLoginModal(): Promise<AuthResult> {
  return new Promise((resolve) => {
    const t = C().ui.auth;
    const modal = createModal("login-modal", `
      <h2>${t.loginHeading}</h2>
      <div class="auth-error" style="display:none"></div>
      <div class="form-group">
        <label for="login-user">${t.usernameOrEmail}</label>
        <input type="text" id="login-user" autocomplete="username" maxlength="255">
      </div>
      <div class="form-group">
        <label for="login-pass">${t.password}</label>
        <input type="password" id="login-pass" autocomplete="current-password" maxlength="128">
      </div>
      <button class="choice-btn action" id="login-submit">${t.submitLogin}</button>
      <button class="choice-btn" id="login-cancel" style="margin-top:0.5rem">${t.cancel}</button>
    `);

    const submit = modal.querySelector("#login-submit") as HTMLButtonElement;
    const cancel = modal.querySelector("#login-cancel") as HTMLButtonElement;

    submit.addEventListener("click", async () => {
      const userInput = (modal.querySelector("#login-user") as HTMLInputElement).value.trim();
      const passInput = (modal.querySelector("#login-pass") as HTMLInputElement).value;
      if (!userInput || !passInput) { showError("login-modal", t.fillAllFields); return; }

      submit.disabled = true;
      submit.textContent = "...";
      const res = await login(userInput, passInput);
      if (res.ok) {
        removeModal("login-modal");
        resolve({ loggedIn: true, isGuest: false, username: userInput });
      } else {
        showError("login-modal", res.error || t.loginFailed);
        submit.disabled = false;
        submit.textContent = t.submitLogin;
      }
    });

    cancel.addEventListener("click", () => {
      removeModal("login-modal");
      resolve({ loggedIn: false, isGuest: false });
    });
  });
}

/** Show register modal */
export function showRegisterModal(): Promise<AuthResult> {
  return new Promise((resolve) => {
    const t = C().ui.auth;
    const modal = createModal("register-modal", `
      <h2>${t.registerHeading}</h2>
      <div class="auth-error" style="display:none"></div>
      <div class="form-group">
        <label for="reg-user">${t.username}</label>
        <input type="text" id="reg-user" autocomplete="username" maxlength="60">
      </div>
      <div class="form-group">
        <label for="reg-email">${t.email}</label>
        <input type="email" id="reg-email" autocomplete="email" maxlength="255">
      </div>
      <div class="form-group">
        <label for="reg-pass">${t.passwordMin}</label>
        <input type="password" id="reg-pass" autocomplete="new-password" maxlength="128">
      </div>
      <div class="form-group">
        <label for="reg-pass2">${t.passwordConfirm}</label>
        <input type="password" id="reg-pass2" autocomplete="new-password" maxlength="128">
      </div>
      <button class="choice-btn action" id="reg-submit">${t.submitRegister}</button>
      <button class="choice-btn" id="reg-cancel" style="margin-top:0.5rem">${t.cancel}</button>
    `);

    const submit = modal.querySelector("#reg-submit") as HTMLButtonElement;
    const cancel = modal.querySelector("#reg-cancel") as HTMLButtonElement;

    submit.addEventListener("click", async () => {
      const user = (modal.querySelector("#reg-user") as HTMLInputElement).value.trim();
      const email = (modal.querySelector("#reg-email") as HTMLInputElement).value.trim();
      const pass = (modal.querySelector("#reg-pass") as HTMLInputElement).value;
      const pass2 = (modal.querySelector("#reg-pass2") as HTMLInputElement).value;

      if (!user || !email || !pass) { showError("register-modal", t.fillAllFields); return; }
      if (pass !== pass2) { showError("register-modal", t.passwordMismatch); return; }
      if (pass.length < 8) { showError("register-modal", t.passwordTooShort); return; }

      submit.disabled = true;
      submit.textContent = "...";
      const res = await register(user, email, pass);
      if (res.ok) {
        removeModal("register-modal");
        resolve({ loggedIn: true, isGuest: false, username: user });
      } else {
        showError("register-modal", res.error || t.registerFailed);
        submit.disabled = false;
        submit.textContent = t.submitRegister;
      }
    });

    cancel.addEventListener("click", () => {
      removeModal("register-modal");
      resolve({ loggedIn: false, isGuest: false });
    });
  });
}

/** Show highscores modal with category tabs */
export async function showHighscoresModal(scores: Array<{
  player_name: string; player_role: string; score: number;
  days_survived: number; meetings_survived: number; final_salary: number;
  longest_meeting?: number;
}>, onCategoryChange?: (cat: string) => Promise<Array<{
  player_name: string; player_role: string; score: number;
  days_survived: number; meetings_survived: number; final_salary: number;
  longest_meeting?: number;
}>>): Promise<void> {
  return new Promise((resolve) => {
    function buildRows(entries: typeof scores): string {
      let rows = "";
      entries.forEach((s, i) => {
        const medal = i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : `${i + 1}.`;
        rows += `<tr>
          <td>${medal}</td>
          <td>${escapeHtml(s.player_name)}</td>
          <td>${escapeHtml(s.player_role)}</td>
          <td>${s.score}</td>
          <td>${s.days_survived}d / ${s.meetings_survived}m</td>
          <td>${s.final_salary}€</td>
        </tr>`;
      });
      return rows;
    }

    const rows = buildRows(scores);
    const t = C().ui.highscores;
    const emptyRow = `<tr><td colspan='6'>${t.empty}</td></tr>`;

    const modal = createModal("highscores-modal", `
      <h2>${t.heading}</h2>
      <div class="hs-category-tabs">
        <button class="hs-tab active" data-cat="score">${t.tabScore}</button>
        <button class="hs-tab" data-cat="days">${t.tabDays}</button>
        <button class="hs-tab" data-cat="salary">${t.tabSalary}</button>
        <button class="hs-tab" data-cat="meetings">${t.tabMeetings}</button>
      </div>
      <div class="highscore-table-wrap">
        <table class="highscore-table">
          <thead><tr><th>#</th><th>${t.colName}</th><th>${t.colRole}</th><th>${t.colScore}</th><th>${t.colDaysMeetings}</th><th>${t.colSalary}</th></tr></thead>
          <tbody id="hs-tbody">${rows || emptyRow}</tbody>
        </table>
      </div>
      <button class="choice-btn continue" id="hs-close" style="margin-top:1rem">${t.close}</button>
    `);

    // Tab switching
    if (onCategoryChange) {
      modal.querySelectorAll(".hs-tab").forEach((btn) => {
        btn.addEventListener("click", async () => {
          modal.querySelectorAll(".hs-tab").forEach((b) => b.classList.remove("active"));
          btn.classList.add("active");
          const cat = (btn as HTMLElement).dataset.cat || "score";
          const newScores = await onCategoryChange(cat);
          const tbody = modal.querySelector("#hs-tbody");
          if (tbody) {
            const newRows = buildRows(newScores);
            tbody.innerHTML = newRows || emptyRow;
          }
        });
      });
    }

    modal.querySelector("#hs-close")!.addEventListener("click", () => {
      removeModal("highscores-modal");
      resolve();
    });
  });
}

function escapeHtml(str: string): string {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}

/** Initialize auth state — auto-login if token exists, or create guest session */
export async function initAuth(): Promise<AuthResult> {
  if (isLoggedIn()) {
    const res = await getMe();
    if (res.ok && res.data) {
      return {
        loggedIn: true,
        isGuest: res.data.is_guest,
        username: res.data.username || undefined,
      };
    }
    // Token invalid, clear it
    logout();
  }
  return { loggedIn: false, isGuest: false };
}

export async function ensureGuestSession(): Promise<void> {
  if (!isLoggedIn()) {
    await loginAsGuest();
  }
}
